import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Icon,
  alpha,
  useTheme,
} from "@mui/material";
import { VoucherDetailProps } from "../types";
import {
  getVoucherTypeConfig,
  getStatusConfig,
  getPaymentMethodLabel,
  formatCurrency,
} from "../utils/voucherUtils";
import { useBranch } from "../../../components/BranchContext";
import moment from "moment";

export const VoucherDetailDialog: React.FC<VoucherDetailProps> = ({
  open,
  onClose,
  voucher,
  onReverse,
  onEdit,
  onDelete,
  onPost,
  onDownloadPDF,
}) => {
  const theme = useTheme();
  const { selectedBranch } = useBranch();

  if (!voucher) return null;

  const typeConfig = getVoucherTypeConfig(voucher.voucher_type);
  const statusConfig = getStatusConfig(voucher.status);
  const branchName = voucher.branch_id
    ? `Branch ${voucher.branch_id}`
    : "Organization";

  // Create a compatible voucher object for handlers that expect IVoucherListItem
  // TODO: Update handlers to accept IVoucherDetail or create proper conversion
  const voucherListItem = {
    ...voucher,
    payment_method: voucher.payment_method as
      | "cash"
      | "bank_transfer"
      | "cheque"
      | "card"
      | "online"
      | undefined,
    attachments_count: 0, // Default for missing properties
    linked_documents_count: 0, // Default for missing properties
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: alpha(typeConfig.color, 0.1),
                color: typeConfig.color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon sx={{ fontSize: 24 }}>{typeConfig.icon}</Icon>
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                {voucher.voucher_id}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {typeConfig.label}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              size="small"
              icon={<Icon sx={{ fontSize: 16 }}>{statusConfig.icon}</Icon>}
              label={statusConfig.label}
              sx={{
                bgcolor: alpha(statusConfig.color, 0.1),
                color: statusConfig.color,
                border: `1px solid ${alpha(statusConfig.color, 0.2)}`,
                fontWeight: 600,
              }}
            />
            <IconButton
              onClick={onClose}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  color: "error.main",
                },
              }}
            >
              <Icon>close</Icon>
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Header Information */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Voucher Details
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontWeight: 600 }}
                >
                  Date
                </Typography>
                <Typography variant="body1">
                  {moment(voucher.voucher_date).format("DD MMMM YYYY")}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontWeight: 600 }}
                >
                  Branch
                </Typography>
                <Typography variant="body1">{branchName}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontWeight: 600 }}
                >
                  Posted By
                </Typography>
                <Typography variant="body1">{voucher.posted_by}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              {voucher.beneficiary && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontWeight: 600 }}
                  >
                    Beneficiary
                  </Typography>
                  <Typography variant="body1">{voucher.beneficiary}</Typography>
                </Box>
              )}
              {voucher.linked_entity && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontWeight: 600 }}
                  >
                    Linked Entity
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                    {voucher.linked_entity}
                  </Typography>
                </Box>
              )}
              {voucher.payment_method && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontWeight: 600 }}
                  >
                    Payment Method
                  </Typography>
                  <Typography variant="body1">
                    {getPaymentMethodLabel(voucher.payment_method)}
                  </Typography>
                </Box>
              )}
              {voucher.reference_number && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontWeight: 600 }}
                  >
                    Reference Number
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                    {voucher.reference_number}
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Narration */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontWeight: 600 }}
          >
            Narration
          </Typography>
          <Typography variant="body1" sx={{ mt: 0.5 }}>
            {voucher.narration}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Voucher Lines */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Account Lines
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
                >
                  <TableCell sx={{ fontWeight: 600 }}>Account</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Debit
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Credit
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {voucher.voucher_lines.map((line, index) => (
                  <TableRow key={line.account_id || index}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {line.account_name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontFamily: "monospace",
                          }}
                        >
                          {line.account_code}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {line.description || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", fontWeight: 600 }}
                      >
                        {line.debit ? formatCurrency(line.debit) : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", fontWeight: 600 }}
                      >
                        {line.credit ? formatCurrency(line.credit) : "—"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Total Row */}
                <TableRow
                  sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
                >
                  <TableCell colSpan={2} sx={{ fontWeight: 600 }}>
                    Total
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, fontFamily: "monospace" }}
                  >
                    {formatCurrency(voucher.total_amount)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, fontFamily: "monospace" }}
                  >
                    {formatCurrency(voucher.total_amount)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Timestamps */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            bgcolor: alpha(theme.palette.background.default, 0.5),
            borderRadius: 1,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 600 }}
              >
                Created At
              </Typography>
              <Typography variant="body2">
                {moment(voucher.created_at).format("DD MMM YYYY, HH:mm")}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 600 }}
              >
                Last Updated
              </Typography>
              <Typography variant="body2">
                {moment(voucher.updated_at).format("DD MMM YYYY, HH:mm")}
              </Typography>
            </Grid>
            {voucher.posted_at && (
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontWeight: 600 }}
                >
                  Posted At
                </Typography>
                <Typography variant="body2">
                  {moment(voucher.posted_at).format("DD MMM YYYY, HH:mm")}
                </Typography>
              </Grid>
            )}
            {voucher.reversed_by && (
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontWeight: 600 }}
                >
                  Reversed By
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  {voucher.reversed_by}
                </Typography>
              </Grid>
            )}
            {voucher.journal_entry_id && (
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontWeight: 600 }}
                >
                  Journal Entry
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: "monospace", color: "primary.main" }}
                >
                  {voucher.journal_entry_id}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose}>Close</Button>

        <Button
          startIcon={<Icon>download</Icon>}
          onClick={() => onDownloadPDF(voucherListItem)}
        >
          Download PDF
        </Button>

        {voucher.status === "draft" && (
          <>
            <Button
              variant="outlined"
              startIcon={<Icon>edit</Icon>}
              onClick={() => onEdit(voucherListItem)}
            >
              Edit
            </Button>
            <Button
              variant="contained"
              startIcon={<Icon>check_circle</Icon>}
              onClick={() => onPost(voucherListItem)}
            >
              Post Voucher
            </Button>
          </>
        )}

        {voucher.status === "posted" && (
          <Button
            variant="outlined"
            color="warning"
            startIcon={<Icon>undo</Icon>}
            onClick={() => onReverse(voucherListItem)}
          >
            Reverse
          </Button>
        )}

        {voucher.status === "draft" && (
          <Button
            color="error"
            startIcon={<Icon>delete</Icon>}
            onClick={() => onDelete(voucherListItem)}
          >
            Delete
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
